import React, { useState, useEffect, useCallback } from 'react';
import { maintenanceRequests as mrApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { segmentName } from '../utils/segments';

const STATUS_LABELS = {
  draft: 'Draft',
  sent_to_mgmt: 'Sent to Management',
  quote_received: 'Quote Received',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  work_completed: 'Completed',
};

const STATUS_COLORS = {
  draft: '#6c757d',
  sent_to_mgmt: '#0d6efd',
  quote_received: '#ffc107',
  pending_approval: '#e76f51',
  approved: '#198754',
  work_completed: '#2d6a4f',
};

export default function MaintenanceRequestsPage() {
  const { isAdmin, isChair } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [vendorName, setVendorName] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await mrApi.list(filter || undefined);
      setRequests(data);
    } catch (err) { setMessage(`Error: ${err.message}`); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    try {
      const data = await mrApi.get(id);
      setDetail(data);
      setSelected(id);
      setVendorName(data.vendor_name || '');
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleAction = async (action) => {
    try {
      if (action === 'send') await mrApi.send(selected);
      else if (action === 'quote') await mrApi.recordQuote(selected, vendorName);
      else if (action === 'approve') await mrApi.approve(selected);
      else if (action === 'complete') await mrApi.complete(selected);
      setMessage(`Action "${action}" completed.`);
      load();
      loadDetail(selected);
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="maint-page">
      <h1>Maintenance Requests</h1>
      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      <div className="concern-filters">
        {['', 'draft', 'sent_to_mgmt', 'pending_approval', 'approved', 'work_completed'].map((f) => (
          <button key={f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
            {f ? STATUS_LABELS[f] : 'All'}
          </button>
        ))}
      </div>

      <div className="maint-layout">
        <div className="maint-list">
          {requests.length === 0 ? <p className="empty-state">No maintenance requests.</p> : (
            requests.map((mr) => (
              <div key={mr.id} className={`maint-card ${selected === mr.id ? 'selected' : ''}`}
                onClick={() => loadDetail(mr.id)}>
                <div className="maint-card-header">
                  <strong>{segmentName(mr)}</strong>
                  <span className="maint-status" style={{ background: STATUS_COLORS[mr.status] }}>
                    {STATUS_LABELS[mr.status]}
                  </span>
                </div>
                <p className="maint-desc">{mr.description.substring(0, 100)}{mr.description.length > 100 ? '...' : ''}</p>
                <span className="concern-date">{new Date(mr.created_at).toLocaleDateString()} — {mr.created_by_name}</span>
              </div>
            ))
          )}
        </div>

        {detail && (
          <div className="maint-detail">
            <h2>{segmentName(detail)}</h2>
            <span className="maint-status" style={{ background: STATUS_COLORS[detail.status] }}>
              {STATUS_LABELS[detail.status]}
            </span>

            <div className="maint-info">
              <p><strong>Description:</strong> {detail.description}</p>
              <p><strong>Created by:</strong> {detail.created_by_name} — {new Date(detail.created_at).toLocaleDateString()}</p>
              {detail.vendor_name && <p><strong>Vendor:</strong> {detail.vendor_name}</p>}
              {detail.approved_by_name && <p><strong>Approved by:</strong> {detail.approved_by_name} — {new Date(detail.approved_at).toLocaleDateString()}</p>}
              {detail.completed_at && <p><strong>Completed:</strong> {new Date(detail.completed_at).toLocaleDateString()}</p>}
              {detail.linked_concern && (
                <p><strong>Linked concern:</strong> {detail.linked_concern.description.substring(0, 80)}...</p>
              )}
            </div>

            {/* Workflow actions */}
            <div className="maint-actions">
              {detail.status === 'draft' && (
                <button className="btn-sm btn-primary" onClick={() => handleAction('send')}>
                  Send to Management Company
                </button>
              )}
              {detail.status === 'sent_to_mgmt' && (
                <div>
                  <label>Vendor name:
                    <input type="text" value={vendorName} onChange={(e) => setVendorName(e.target.value)}
                      placeholder="Everthrive" style={{ marginLeft: '0.5rem', padding: '0.3rem' }} />
                  </label>
                  <button className="btn-sm btn-primary" onClick={() => handleAction('quote')} style={{ marginLeft: '0.5rem' }}>
                    Record Quote Received
                  </button>
                </div>
              )}
              {detail.status === 'pending_approval' && (isChair || isAdmin) && (
                <button className="btn-sm btn-primary" onClick={() => handleAction('approve')}>
                  Approve and Notify Management
                </button>
              )}
              {detail.status === 'approved' && (
                <button className="btn-sm btn-primary" onClick={() => handleAction('complete')}>
                  Mark Work Completed
                </button>
              )}
            </div>

            {/* Status history */}
            {detail.history && detail.history.length > 0 && (
              <div className="maint-history">
                <h3>Status History</h3>
                {detail.history.map((h) => (
                  <div key={h.id} className="history-entry">
                    <span className="history-date">{new Date(h.created_at).toLocaleString()}</span>
                    <span className="history-status">{STATUS_LABELS[h.new_status] || h.new_status}</span>
                    <span className="history-user">{h.changed_by_name}</span>
                    {h.notes && <p className="history-notes">{h.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
